import { useGetChartingRecallSettings } from "@src/appV2/Charting/api/useGetChartingRecallSettings";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useWorkplaceAnalyticsFlag } from "@src/appV2/redesign/WorkplaceAnalytics/hooks/useWorkplaceAnalyticsFlag";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { PlacementAccessControlStatus } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useIsInterviewAvailabilityMenuVisible } from "@src/appV2/InterviewSettings/useIsInterviewAvailabilityMenuVisible";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { FACILITY_ROLES, FACILITY_USER_PERMISSIONS } from "@src/constants/facility";
import { evaluateWorkplaceToWorkplaceReferrals } from "@src/containers/workplaceReferAndEarn/utils";
import { useFacilityOnboardingFlag } from "@src/hooks/useFacilityOnboardingFlag";
import { type SessionType } from "@src/modules/interface";
import constate from "constate";
import { useFlags } from "launchdarkly-react-client-sdk";
import includes from "lodash/includes";
import { useSelector } from "react-redux";

import { hasAnyTrueValue } from "./utils";
import { isDefined } from "@clipboard-health/util-ts";
import { AnalyticsType } from "@src/appV2/Analytics/constants";
import { useShouldShowClassicCalendar } from "./classicCalendarCutoff";
import { canAccessWorkplaceAnalytics } from "./workplaceAnalyticsAccess";

export interface WorkplaceMenuPrivileges {
  homeHealthSection?: {
    cases: boolean;
    visitVerification: boolean;
    coverageMap: boolean;
    connectEmr: boolean;
  };
  workplaceOnboarding?: boolean;
  dailyView?: boolean;
  calendar?: boolean;
  chat?: boolean;
  professionalsSection?: {
    myProfessionals: boolean;
    browseProfessionals: boolean;
  };
  permanentHiringSection?: {
    placementsLandingPage: boolean;
    interviewSchedule: boolean;
    applicationAndJobs: boolean;
    browseTalentPool: boolean;
    interviewAvailability: boolean;
  };
  isPermanentHiringSectionLoading?: boolean;
  charting?: boolean;
  analyticsSection?: {
    exploWorkplaceAnalytics: boolean;
    exploCorporateAnalytics: boolean;
  };
  corporateCenterSection?: {
    exploCorporateAnalytics: boolean;
    spendingControls: boolean;
    workplaceAnalytics: boolean;
  };
  liveWorkerETA?: boolean;
  workplaceRulesQuiz?: boolean;
  rulesAndExpectations?: boolean;
  managementSection?: {
    myAccount: boolean;
    teamMembers: boolean;
    internalRoster: boolean;
    requiredDocuments: boolean;
    workplaceInformation: boolean;
    settings: boolean;
  };
  billingSection?: {
    reporting: boolean;
    upcomingCharges: boolean;
    invoiceHistory: boolean;
    contractTerms: boolean;
    workplaceSpendingControls: boolean;
  };
  helpCenter?: boolean;
  referAndEarn?: boolean;
  // Route authorization flags (not for menu visibility)
  routeAuth?: {
    isWorkplaceOrAdmin: boolean;
    hasPermissionToAccessWorkerDirectory: boolean;
    isDocumentAuthorized: boolean;
    hasDefaultWorkplacePermissions: boolean;
    isFacilityInvoiceDisputeAuthorized: boolean;
    isInvoiceViewAuthorized: boolean;
    workplaceSpendingControls: boolean;
    exploAnalytics: boolean;
  };
}

export function canAccessPaymentRequestReview(params: {
  roles?: string[];
  permissions?: string[];
}): boolean {
  const { roles = [], permissions = [] } = params;

  return (
    includes(roles, FACILITY_ROLES.DOCUMENTS) &&
    includes(permissions, FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS)
  );
}

/* eslint-disable complexity */
export function useGetWorkplaceMenuPrivileges(): WorkplaceMenuPrivileges {
  const session = useSelector((state: SessionType) => state.session);
  const { user, profile, type } = session ?? {};
  const { access: roles, permissions } = user ?? {};

  const effectivePerms = useEffectivePermissions();
  const {
    hasAdminPrivileges,
    isCurrentlyAdmin,
    hasDefaultWorkplacePermissions: hasDefaultPerms,
    canViewWorkplaceChat,
    canAccessWorkerDirectory,
    canViewBilling,
    canEditDocuments,
    canViewInvoices,
    hasAdminRole,
  } = effectivePerms;

  const isAdmin = isCurrentlyAdmin;
  const isWorkplace = type === "FACILITY";

  const workplaceOnboarding = useFacilityOnboardingFlag({
    workplaceCreationDate: profile?.createdAt,
    onboardingSteps: profile?.onboardingSteps,
    roles: roles ?? [],
    isAdmin,
    isWorkplace,
  });

  // Use the value from our hook which correctly handles pseudo-login
  const hasDefaultWorkplacePermissions = hasDefaultPerms;

  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const isHomeHealthHiddenMenuItemsEnabled = useCbhFlag(
    CbhFeatureFlag.HOME_HEALTH_MENU_ITEMS_HIDDEN,
    {
      defaultValue: true,
    }
  );

  const hideMenuItemsForHomeHealth =
    isHomeHealthEnabled && isHomeHealthHiddenMenuItemsEnabled && !hasAdminPrivileges;

  const isRecruitmentOnly =
    !hasAdminPrivileges &&
    includes(roles, FACILITY_ROLES.RECRUITMENT) &&
    !hasDefaultWorkplacePermissions;

  const isCoverageMapEnabled = useCbhFlag(CbhFeatureFlag.WORKPLACE_VISIT_HEAT_MAP, {
    defaultValue: false,
  });

  const isConnectEmrEnabled = useCbhFlag(CbhFeatureFlag.CONNECT_EMR, {
    defaultValue: false,
  });

  // Home Health specific menu items (hidden for recruiter-only users)
  const homeHealthCases = isHomeHealthEnabled && !isRecruitmentOnly;
  const homeHealthVisitVerification = isHomeHealthEnabled && !isRecruitmentOnly;
  const homeHealthCoverageMap = isHomeHealthEnabled && !isRecruitmentOnly && isCoverageMapEnabled;
  const homeHealthConnectEmr = isHomeHealthEnabled && !isRecruitmentOnly && isConnectEmrEnabled;
  const homeHealthSection = {
    cases: homeHealthCases,
    visitVerification: homeHealthVisitVerification,
    coverageMap: homeHealthCoverageMap,
    connectEmr: homeHealthConnectEmr,
  };

  const isClassicCalendarException = useCbhFlag(CbhFeatureFlag.CLASSIC_CALENDAR_EXCEPTION, {
    defaultValue: false,
  });

  const dailyView = hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth;

  // Gated on when the workplace first went live, not when the account was created.
  // The hook owns the first-shift lookup so its fetch gate and decision can't drift.
  const canSeeClassicCalendar = useShouldShowClassicCalendar({
    isWorkplace,
    workplaceUserId: profile?.userId,
    workplaceCreatedAt: profile?.createdAt,
    hasAdminPrivileges,
    isClassicCalendarException,
  });

  const calendar =
    hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth && canSeeClassicCalendar;

  // Use the value from our hook which correctly handles permissions
  const chat = canViewWorkplaceChat;

  // Home Health workplaces had My Professionals hidden because the legacy list
  // source (shift-based) excluded HH visits. With the HH-aware professionals
  // list available, My Professionals is restored for HH without un-hiding the
  // other items gated by hideMenuItemsForHomeHealth.
  const myProfessionals = !hideMenuItemsForHomeHealth || isHomeHealthEnabled;

  const hasPermissionToAccessWorkerDirectory = canAccessWorkerDirectory;

  const ldFlags = useFlags();
  const hideBrowserWorkers = ldFlags?.[CbhFeatureFlag.HIDE_BROWSER_WORKERS];

  const browseProfessionals =
    hasPermissionToAccessWorkerDirectory && !isHomeHealthEnabled && !hideBrowserWorkers;

  const professionalsSection = { myProfessionals, browseProfessionals };

  const {
    isEnabled: isPlacementsEnabled,
    canBrowseTalentPool,
    accessLevel: placementsAccessLevel,
  } = useGetPlacementsAccessLevel();

  const isInterviewAvailabilityVisible = useIsInterviewAvailabilityMenuVisible();

  const placementsLandingPage = placementsAccessLevel === PlacementAccessControlStatus.LANDING_PAGE;

  const interviewSchedule = isPlacementsEnabled;

  const applicationAndJobs = isPlacementsEnabled;

  const browseTalentPool = canBrowseTalentPool;

  const interviewAvailability = isPlacementsEnabled && isInterviewAvailabilityVisible;

  const hasPermissionToRecruit =
    hasAdminPrivileges ||
    includes(roles, FACILITY_ROLES.RECRUITMENT) ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT);

  const { data: facilityProfileData, isInitialLoading: isInitialLoadingFacilityProfile } =
    useGetFacilityProfile(profile?.userId ?? "", "placementSettings.isProspect", {
      enabled: isWorkplace && isDefined(profile?.userId) && hasPermissionToRecruit,
    });
  const isProspect = facilityProfileData?.placementSettings?.isProspect ?? false;

  const permanentHiringSection = {
    placementsLandingPage,
    interviewSchedule,
    applicationAndJobs,
    browseTalentPool,
    interviewAvailability,
  };

  const { chartingRecallEnabled } = useGetChartingRecallSettings({
    workplaceId: profile?.userId,
    options: { enabled: isWorkplace },
  });

  const canAccessCharting =
    isAdmin ||
    !includes(roles, FACILITY_ROLES.DOCUMENTS) ||
    includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_CHARTING);

  const charting = chartingRecallEnabled && canAccessCharting && !isRecruitmentOnly;

  const hasCorporateAdminRole = includes(roles, FACILITY_ROLES.CORPORATE_ADMIN);

  const workplaceAnalyticsFlag = useWorkplaceAnalyticsFlag();
  const isWorkplaceAnalyticsAccessible = canAccessWorkplaceAnalytics({
    flag: workplaceAnalyticsFlag,
    hasAdminPrivileges,
    canViewBilling,
  });

  // Legacy Explo dashboards — superseded by the new workplace analytics dashboard
  // only for users who can actually access it (e.g. not for workplace users while
  // the dashboard is internal-use-only).
  const exploAnalyticsConfig = useCbhFlag(CbhFeatureFlag.FACILITY_EXPLO_ANALYTICS_ENABLED, {
    defaultValue: [],
  });
  const isExploAnalyticsVisible =
    !isWorkplaceAnalyticsAccessible && exploAnalyticsConfig.length > 0;
  const hasExploAnalyticsPermissions =
    isExploAnalyticsVisible && (hasAdminPrivileges || includes(roles, FACILITY_ROLES.ADMIN));
  const hasExploCorporateAnalyticsEmbed = isDefined(
    exploAnalyticsConfig.find((e) => e.type === AnalyticsType.CORPORATE_FLEX)
  );
  const exploWorkplaceAnalytics =
    hasExploAnalyticsPermissions &&
    isDefined(exploAnalyticsConfig.find((e) => e.type === AnalyticsType.WORKPLACE_FLEX));
  // Within the Corporate Center the corporate Explo dashboard is not role-gated:
  // any user at the workplace may view it.
  const exploCorporateAnalytics = isExploAnalyticsVisible && hasExploCorporateAnalyticsEmbed;
  const analyticsSection = {
    exploWorkplaceAnalytics,
    exploCorporateAnalytics: hasExploAnalyticsPermissions && hasExploCorporateAnalyticsEmbed,
  };

  // Corporate Center section
  const spendingControlsConfig = useCbhFlag(CbhFeatureFlag.CONFIGURE_SPENDING_CONTROLS, {
    defaultValue: { enabled: false },
  });
  const isSpendingControlsEnabled = spendingControlsConfig.enabled;
  const isSpendingControlsInternalOnly =
    spendingControlsConfig.enabled && spendingControlsConfig.internalUseOnly;
  // When internal-only, only Clipboard employees (admins) can access spending controls
  const canAccessCorporateSpendingControls = isSpendingControlsInternalOnly
    ? hasAdminPrivileges
    : hasAdminPrivileges || hasCorporateAdminRole;

  const shouldShowCorporateCenter =
    (isSpendingControlsEnabled && canAccessCorporateSpendingControls) ||
    exploCorporateAnalytics ||
    isWorkplaceAnalyticsAccessible;
  const corporateCenterSection = shouldShowCorporateCenter
    ? {
        exploCorporateAnalytics,
        spendingControls: isSpendingControlsEnabled && canAccessCorporateSpendingControls,
        workplaceAnalytics: isWorkplaceAnalyticsAccessible,
      }
    : undefined;

  const liveWorkerETA = hasDefaultWorkplacePermissions && !isHomeHealthEnabled;

  // When enabled, replaces the Workplace Rules Quiz tab and legacy Workplace Information fallback below.
  const rulesAndExpectations =
    (ldFlags?.[CbhFeatureFlag.RELEASE_REDESIGNED_RULES_AND_EXPECTATIONS_ADMIN_PAGE] ?? false) &&
    !isRecruitmentOnly;

  const workplaceRulesQuiz =
    (ldFlags?.[CbhFeatureFlag.ROLLOUT_QUIZ_V2_FACILITY_RULES_AI] ?? false) &&
    !isRecruitmentOnly &&
    !rulesAndExpectations;

  const myAccount = !hasAdminPrivileges;

  const teamMembers =
    hasAdminPrivileges ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    includes(roles, FACILITY_ROLES.INVOICES);

  const isInternalRosterEnabled = ldFlags?.[CbhFeatureFlag.CLIPBOARD_ROSTER_ENABLED] === true;
  // Match BE auth: any workplace user can manage the roster in v0. The BE
  // checks workplace membership only (no role check), and the FE matches.
  const internalRoster = isInternalRosterEnabled;

  const workplaceInformation =
    !workplaceRulesQuiz && !rulesAndExpectations && hasDefaultWorkplacePermissions;

  const settings =
    (includes(roles, FACILITY_ROLES.ADMIN) || hasAdminPrivileges) && !hideMenuItemsForHomeHealth;

  const managementSection = {
    myAccount,
    teamMembers,
    internalRoster,
    requiredDocuments: true,
    workplaceInformation,
    settings,
  };

  const hasBillingRights = canViewBilling;
  const reporting =
    (hasAdminPrivileges ||
      includes(roles, FACILITY_ROLES.ADMIN) ||
      includes(roles, FACILITY_ROLES.INVOICES) ||
      includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
      includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_REPORTING)) &&
    !hideMenuItemsForHomeHealth;

  // Admin users need specific employee roles that the backend /api/billing/upcoming-charges
  // endpoint requires. Without these roles, the API returns 403.
  const adminCanAccessUpcomingCharges =
    !hasAdminPrivileges ||
    hasAdminRole(EMPLOYEE_ROLES.ADMINISTRATIVE) ||
    hasAdminRole(EMPLOYEE_ROLES.BILLING) ||
    hasAdminRole(EMPLOYEE_ROLES.SHIFT_VERIFICATION);

  const upcomingCharges =
    adminCanAccessUpcomingCharges &&
    (hasBillingRights ||
      includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES) ||
      includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
      canAccessPaymentRequestReview({ roles, permissions }));

  const invoiceHistory =
    hasBillingRights || includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES);

  const hasViewContractTermsRights =
    hasBillingRights || includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS);

  const contractTerms = hasViewContractTermsRights && !hideMenuItemsForHomeHealth;

  // Workplace spending controls: enabled for facility admins and staffing coordinators
  // who are NOT corporate admins (they use the Corporate Center page instead)
  const workplaceSpendingControls =
    isSpendingControlsEnabled &&
    !hasAdminPrivileges &&
    !hasCorporateAdminRole &&
    (includes(roles, FACILITY_ROLES.ADMIN) || includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT));

  const billingSection = {
    reporting,
    upcomingCharges,
    invoiceHistory,
    contractTerms,
    workplaceSpendingControls,
  };

  // Hide Help Center for Home Healthcare and Hospice workplace types
  const helpCenter = !isHomeHealthEnabled;

  const referAndEarn =
    !isHomeHealthEnabled &&
    evaluateWorkplaceToWorkplaceReferrals(ldFlags, user, isWorkplace, hasAdminPrivileges);

  // Route authorization (mirrors legacy appLayout.tsx checks)
  const isWorkplaceOrAdmin = isWorkplace || hasAdminPrivileges;

  const isDocumentAuthorized = canEditDocuments;

  // Facilities can dispute invoices if they have invoice permissions, but not if they're admins pseudo-logged in
  const isFacilityInvoiceDisputeAuthorized = isWorkplace && !hasAdminPrivileges && canViewInvoices;

  const isInvoiceViewAuthorized = canViewInvoices;

  const canAccessWorkplaceSpendingControls =
    isSpendingControlsEnabled &&
    !hasCorporateAdminRole &&
    (isAdmin ||
      includes(roles, FACILITY_ROLES.ADMIN) ||
      includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT));

  const routeAuth = {
    isWorkplaceOrAdmin,
    hasPermissionToAccessWorkerDirectory,
    isDocumentAuthorized: Boolean(isDocumentAuthorized),
    hasDefaultWorkplacePermissions,
    isFacilityInvoiceDisputeAuthorized: Boolean(isFacilityInvoiceDisputeAuthorized),
    isInvoiceViewAuthorized: Boolean(isInvoiceViewAuthorized),
    workplaceSpendingControls: canAccessWorkplaceSpendingControls,
    // The Explo dashboard route serves both the Analytics section and the
    // Corporate Center entry, which have different role gates.
    exploAnalytics: exploWorkplaceAnalytics || exploCorporateAnalytics,
  };

  if (!isWorkplace) {
    return { routeAuth };
  }

  return {
    homeHealthSection: isHomeHealthEnabled ? homeHealthSection : undefined,

    workplaceOnboarding: isHomeHealthEnabled ? false : workplaceOnboarding,
    dailyView,
    calendar,
    chat,

    professionalsSection:
      (user
        ? hasAdminPrivileges ||
          includes(user.access, FACILITY_ROLES.ADMIN) ||
          includes(user.access, FACILITY_ROLES.INVOICES) ||
          includes(user.access, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
          includes(user.access, FACILITY_ROLES.DOCUMENTS)
        : false) && hasAnyTrueValue(professionalsSection)
        ? professionalsSection
        : undefined,

    permanentHiringSection:
      hasPermissionToRecruit &&
      hasAnyTrueValue(permanentHiringSection) &&
      !(isProspect && !hasAdminPrivileges)
        ? permanentHiringSection
        : undefined,

    isPermanentHiringSectionLoading: isInitialLoadingFacilityProfile && !hasAdminPrivileges,

    charting,

    analyticsSection:
      hasExploAnalyticsPermissions && hasAnyTrueValue(analyticsSection)
        ? analyticsSection
        : undefined,

    corporateCenterSection,

    liveWorkerETA,

    workplaceRulesQuiz,

    rulesAndExpectations,

    managementSection: hasAnyTrueValue(managementSection) ? managementSection : undefined,

    billingSection: hasAnyTrueValue(billingSection) ? billingSection : undefined,

    helpCenter,
    referAndEarn,
    routeAuth,
  };
}
/* eslint-enable complexity */

export const [WorkplaceMenuPrivilegesProvider, useWorkplaceMenuPrivilegesProviderContext] =
  constate(useGetWorkplaceMenuPrivileges);
